const { Core, Utils, DOMEvents, Events, TopLayerStack } = Global;
const { $fetch, $replaceContent, $active } = Utils;

const ENTER_KEYCODE = 13;

const animationsComplete = element =>
  Promise.allSettled(
    element.getAnimations().map(animation => animation.finished)
  );

customElements.define('modal-container', class extends Core {
  elements = { $: ['dialog', 'content', 'spinner'] }

  propTypes = {
    'collection-utm': String,
    'remote-url': String,
    'remote-target': String,
    'remote-detach': Boolean,
    'collection-target': String,
  }

  render() {
    this.$('dialog', { click: this._handleBackdropClick });
    this.$({ 'modal-close': this._close, 'modal-open': this._open });
  }

  open() { this.dispatchEvent(DOMEvents.MODAL_OPEN); }
  close() { this.dispatchEvent(DOMEvents.MODAL_CLOSE); }

  refreshLayer() {
    this.$('dialog').close();
    this.$('dialog').showModal();
  }

  _handleBackdropClick({ target }) {
    if (target === this.$('dialog')) this.dispatchEvent(DOMEvents.MODAL_CLOSE);
  }

  async _loadRemoteContent() {
    try {
      this.$remoteContent = await $fetch(this.prop('remote-url'), {
        before: this._loading(true),
        after: this._loading(false),
        select: this.prop('remote-target')
      });
    } catch (err) {
      console.error(err);
    }
  }

  _loading(state) {
    return () => { $active(this.$('spinner'), state); }
  }

  _attachRemoteContent() {
    if (this.$remoteContent) this.$('content').append(this.$remoteContent);
  }

  _detachRemoteContent() {
    if (this.$remoteContent && this.prop('remote-detach')) {
      this.$remoteContent.remove();
    }
    this._disconnectObservers();
  }

  async _handleRemoteContent() {
    if (this.prop('remote-url') && this.prop('remote-target') && !this.$remoteContent) {
      await this._loadRemoteContent();
    }
    this._attachRemoteContent();
    this._setupLinkPrefixing();
  }

  // ---------- Prefix logic + observers ----------
  _applyCollectionPrefixToHref(rawHref) {
    const collectionTarget = this.prop('collection-target');
    if (!collectionTarget || !rawHref) return null;

    const url = new URL(rawHref, window.location.origin);
    const base = '/' + String(collectionTarget).replace(/^\/+|\/+$/g, ''); // "/collections/pain-relief"
    const path = url.pathname.replace(/^\/+/, '');                          // "products/slug"

    if (('/' + path).startsWith(base + '/')) {
      // Already prefixed; no change.
      return url.pathname + url.search + url.hash;
    }

    // --- NEW: Add UTM param if defined ---
    const collectionUTM = this.prop('collection-utm');
    let utm = '';
    if (collectionUTM) {
        // Add "&view=collectionUTM" properly (after ? or &)
        const separator = url.search ? '&' : '?';
        utm = `${separator}view=${encodeURIComponent(collectionUTM)}`;
    }

      return `${base}/${path}${url.search}${utm}${url.hash}`;
  }

  _findQuickViewLink() {
    // 1) your working selector, 2) id fallback, 3) generic
    return this.querySelector('#s_product-quick-view-view-more-link > a')
        || this.querySelector('product-variant-listener[id$="-view-more-link"] > a')
        || this.querySelector('#ProductQuickView product-variant-listener a');
  }

  _ensureLinkPrefixed(link) {
    if (!link) return;
    const current = link.getAttribute('href') || '';
    const patched = this._applyCollectionPrefixToHref(current);
    if (patched && patched !== (new URL(current, window.location.origin).pathname + new URL(current, window.location.origin).search + new URL(current, window.location.origin).hash)) {
      link.setAttribute('href', patched);
    }
  }

  _setupLinkPrefixing() {
    // Initial apply
    const link = this._findQuickViewLink();
    this._ensureLinkPrefixed(link);

    // Set up observers to persist the prefix across variant changes / re-renders
    this._disconnectObservers();

    // Observe the container for link replacement
    const container = this.querySelector('#ProductQuickView') || this.$('content');
    if (!container) return;

    this._containerObserver = new MutationObserver(() => {
      // Re-find (in case node was replaced)
      const l = this._findQuickViewLink();
      if (!l) return;
      this._ensureLinkPrefixed(l);

      // Also ensure we’re watching href changes on the current link
      if (this._linkObserverTarget !== l) {
        this._observeHref(l);
      }
    });
    this._containerObserver.observe(container, {
      childList: true,
      subtree: true
    });

    // Also watch href mutations on the current link (attribute changes)
    if (link) this._observeHref(link);
  }

  _observeHref(link) {
    if (this._linkObserver) this._linkObserver.disconnect();
    this._linkObserverTarget = link;

    this._linkObserver = new MutationObserver((records) => {
      for (const r of records) {
        if (r.type === 'attributes' && r.attributeName === 'href') {
          this._ensureLinkPrefixed(link);
        }
      }
    });
    this._linkObserver.observe(link, { attributes: true, attributeFilter: ['href'] });
  }

  _disconnectObservers() {
    this._containerObserver?.disconnect();
    this._containerObserver = null;

    this._linkObserver?.disconnect();
    this._linkObserver = null;
    this._linkObserverTarget = null;
  }
  // ---------- /prefix logic + observers ----------

  _onClose(cb) { this.addEventListener('modal-close', cb, { once: true }); }

  async _open(e) {
    if (this.$('dialog').open) return;

    TopLayerStack.add(this.$('dialog'), this._onClose.bind(this));
    this.$('dialog').showModal();

    await this._handleRemoteContent();
    this.setAttribute('opening', '');
    await animationsComplete(this.$('content'));
    this.removeAttribute('opening');

    this.$('dialog').dispatchEvent(new CustomEvent('modal-open', { bubbles: true }));
  }

  async _close(e) {
    e?.preventDefault();
    if (!this.$('dialog').open) return;

    TopLayerStack.remove();
    this.setAttribute('closing', '');
    await animationsComplete(this.$('content'));
    this.$('dialog').close();
    this.removeAttribute('closing');

    this._detachRemoteContent();
  }
});

customElements.define('modal-open', class extends Core {
  propTypes = { target: String, fallback: String }

  render() {
    this.$target = document.getElementById(this.prop('target'));
    this._handleClick();
  }

  async _handleClick() {
    await customElements.whenDefined('modal-container');
    this.$({ click: this._openTargetModal, keydown: this._openOnEnterPressed });
  }

  _openOnEnterPressed(e) {
    if (e.keyCode === ENTER_KEYCODE) this._openTargetModal();
  }

  _openTargetModal() {
    if (!this.$target) {
      if (this.prop('fallback')) window.location.href = this.prop('fallback');
      return;
    }
    this.$target.dispatchEvent(DOMEvents.MODAL_OPEN);
  }
});

customElements.define('modal-close', class extends Core {
  render() { this.$({ click: this._emitClose }); }
  _emitClose() { this.dispatchEvent(DOMEvents.MODAL_CLOSE); }
});
